import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';
import VideoComponent from '../../core/Video/VideoAnimation';
import {
	IconBack,
	IconCircleCheck,
	IconCircleExclamation,
	IconCircleHelper,
	IconCircleInfo,
	IconCkeckSolid,
	IconDropDownArrow,
} from '../Icons';

import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { postApiPriceComparator, postApiTaricTaricNomenclature } from '../../api';
import CustomMultiSelect from '../../core/MultiSelect/multi-select';
import { useRemainingPacks } from '../../hooks/useRemainingPacks';
import { useProUser } from '../../context/ProUserContext';
import i18n from '../../i18n';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import PopUp from '../../shared/PopUp/PopUp';
import { DataOption } from '../ExporterTest/DataOptions';
import { IconComparadorSucces } from '../Icons';
import IconPro from '../Icons/IconPro';
import { usePriceComparatorForm } from './hooks/usePriceComparatorForm';
import ModalLegal from './ModalLegal';
import './PriceComparatorDetailProd.css';
import './PriceComparatorPopupResume.css';
import './PriceComparatorPopupSuccess.css';
import { useGetCountries } from './hooks/useGetCountries';
import type { Option } from '../../core/MultiSelect/multi-select';

// Agregar estos tipos
type CountryGroup = {
	nameEs: string;
	nameEn: string;
	countries: Array<{
		id: string;
		name: string;
		code: string | null;
	}>;
};

type ApiResponse = {
	results: CountryGroup[];
	hasMoreResults: boolean | null;
	continuationToken: string | null;
	status: string;
	successMessage: string;
	errors: string[];
	validationErrors: any[];
};

export const PriceComparatorDetailProd = ({
	currentPack,
	setCurrentStep,
}: {
	currentPack: { userSubscriptionId: string; productCount?: number; countryCount?: number } | string;
	setCurrentStep: (step: string) => void;
}) => {
	const { t } = useTranslation();
	const lang = i18n.language || 'en'; // Idioma actual o inglés por defecto
	const { PaisesCustom } = DataOption();
	const [isOpenResume, setIsOpenResume] = useState(false);
	const [isOpenSuccess, setIsOpenSuccess] = useState(false);
	const [isLoadingTarif, setIsLoadingTarif] = useState(false); // Estado de carga
	const [tarifDescription, setTarifDescription] = useState<string | null>(null);
	const [isPopupTarifOpen, setIsPopupTarifOpen] = useState(false);
	const [isValidTarif, setIsValidTarif] = useState(false); // Estado de validación
	const [currentValidatingProductIndex, setCurrentValidatingProductIndex] = useState<number>(0);
	const [validatedTariffCodes, setValidatedTariffCodes] = useState<number[]>([]);
	const [open, setOpen] = useState(false);
	const [isLoadingCompare, setIsLoadingCompare] = useState(false); // Estado de loading para comparación

	const { data, loading, error } = useGetCountries() as {
		data: ApiResponse | null;
		loading: boolean;
		error: Error | null;
	};

	// Transformar los datos de la API al formato que espera CustomMultiSelect
	const transformedCountries = useMemo(() => {
		if (!data?.results) return [];

		const options: Option[] = [];

		data.results.forEach((group: CountryGroup) => {
			// Usar nameEs o nameEn según idioma
			const groupTitle = lang === 'en' ? group.nameEn : group.nameEs;
			const groupValue = groupTitle?.toLowerCase().replace(/\s+/g, '_') || 'group';

			// Agregar el grupo como opción principal
			options.push({ label: groupTitle, value: groupValue });

			// Agregar los países como hijos
			if (group.countries && Array.isArray(group.countries)) {
				group.countries.forEach((country) => {
					if (country.name && (country.code || country.id)) {
						options.push({
							label: country.name,
							value: country.code || country.id,
							parent: groupValue,
						});
					}
				});
			}
		});

		return options;
	}, [data, lang]);

	const handleClickOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};

	const {
		data: remainingPacks,
		loading: loadingCalcs,
		error: errorCalcs,
		refetch: refetchRemainingCalcs,
	} = useRemainingPacks(process.env.REACT_APP_PRICE_COMPARATOR_ID || '354');

	// Usar el hook personalizado para manejar el formulario
	const {
		formData,
		errors,
		validateForm,
		handleInputChange,
		handleCountriesChange,
		resetForm,
		isFormComplete,
		maxProducts,
		maxCountries,
	} = usePriceComparatorForm(currentPack); // Pasar el currentPack

	// console.log('🚀 ~ PriceComparatorDetailProd ~ currentPack:', currentPack);
	// Función para manejar el click en "Comparar precios"
	const handleComparePrices = () => {
		if (validateForm()) {
			setIsOpenResume(true);
		}
	};

	// Hook para verificar estado pro del usuario
	const { refetchProStatus } = useProUser();

	const handleConfirmAndCompare = async () => {
		// console.log('🚀 ~ handleConfirmAndCompare ~ formData:', formData);

		setIsLoadingCompare(true); // Activar loading

		try {
			// Transformar los datos del formulario al formato esperado por la API
			const transformedData = {
				products: formData.products.map((product) => ({
					productName: product.productName,
					containerType: product.containerType,
					tariffCode: product.tariffCode,
					unit: product.unit,
					weightVolume: parseFloat(product.weightVolume) || 0, // Convertir a número
					currency: 'EUR', // Moneda fija como EUR
					price: parseFloat(product.price) || 0, // Convertir a número
				})),
				countryCodes: formData.selectedCountries.map((country) =>
					typeof country === 'string' ? country : country.value,
				), // Extraer solo los códigos de país
				userSubscriptionId: typeof currentPack === 'string' ? currentPack : currentPack.userSubscriptionId,
			};

			console.log('🚀 ~ transformedData:', transformedData);

			// Llamar a la API con los datos transformados
			const response = await postApiPriceComparator(transformedData);

			// console.log('🚀 ~ API Response:', response);

			// Si la llamada es exitosa, cerrar el popup de resumen y mostrar el de éxito
			setIsOpenResume(false);
			setIsOpenSuccess(true);
		} catch (error) {
			console.error('Error calling price comparator API:', error);

			// Mostrar algún mensaje de error
		} finally {
			setIsLoadingCompare(false); // Desactivar loading

			// Refrescar el estado pro del usuario después de la comparación
			// ya que el servicio resta un uso del pack
			// Agregar un delay para asegurar que la API haya procesado la actualización
			setTimeout(() => {
				console.log('🔄 Refreshing pro status after comparison');
				refetchProStatus();
			}, 800);
		}
	};
	const handleCloseTarifPopup = () => {
		setIsPopupTarifOpen(false);
	};

	const handleOpenTarifPopup = async (productIndex: number) => {
		setIsLoadingTarif(true);
		setCurrentValidatingProductIndex(productIndex);

		// formatear el tarifCode para que solo sea numeros
		const formattedTarifCode = formData.products[productIndex].tariffCode.replace(/[^0-9]/g, '');

		try {
			const response = await postApiTaricTaricNomenclature({ goodsCode: formattedTarifCode });
			if (response?.results && response.results.length > 0) {
				// Formatear las descripciones en estructura jerárquica con divs y spans
				const formattedDescriptions = response.results
					.map((item) => {
						if (!item?.goodsCode || !item?.hierPos) return '';
						const indent = item.indent || '';
						const fullCode = item.goodsCode.split(' ')[0];
						// Calcula cuántos dígitos mostrar basado en hierPos
						const digitsToShow = Math.ceil(item.hierPos / 2) * 2;
						const code = fullCode.substring(0, digitsToShow);
						const itemDescription = lang === 'en' ? item.descriptionEN : item.descriptionES;
						return `<div class="tariff-item">
						<span class="tariff-code">${code}</span>
						<span class="tariff-description">${indent}${itemDescription}</span>
					</div>`;
					})
					.join('');

				if (formattedDescriptions) {
					setTarifDescription(formattedDescriptions || '');
					setIsValidTarif(true);
					handleInputChange(productIndex, 'tariffCode', formattedTarifCode);
					// Marcar este producto como validado
					setValidatedTariffCodes((prev) => (prev.includes(productIndex) ? prev : [...prev, productIndex]));
					setIsPopupTarifOpen(true);
				} else {
					setIsValidTarif(false);
					// Remover este producto de los validados
					setValidatedTariffCodes((prev) => prev.filter((index) => index !== productIndex));
					setIsPopupTarifOpen(true);
				}
			} else {
				setIsValidTarif(false);
				// Remover este producto de los validados
				setValidatedTariffCodes((prev) => prev.filter((index) => index !== productIndex));
				// setError('tarifCode', { type: 'invalid', message: t('price_calculator.step_1.error_message') });
				setIsPopupTarifOpen(true);
			}
		} catch (error) {
			setIsValidTarif(false);
			// Remover este producto de los validados
			setValidatedTariffCodes((prev) => prev.filter((index) => index !== productIndex));
			// setError('tarifCode', { type: 'invalid', message: t('price_calculator.step_1.error_message') });
			setIsPopupTarifOpen(true);
			// console.error('Error fetching tarif description:', error);
		} finally {
			setIsLoadingTarif(false);
		}
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	if (loadingCalcs) {
		return <CircularLoading />;
	}
	return (
		<>
			<div className="price-comp__row">
				<div className="exporter-test-information__header">
					<div className="price-calc__principal-title">
						<span>{t('price_comparator.header.title')}</span>
						<CustomToolTip
							title=""
							tooltipText={
								<span>
									{t('price_comparator.header.tooltip')}{' '}
									<button
										// onClick={handleClickOpen}
										className="price-calc__tootip-link">
										{t('price_comparator.header.button')}
									</button>
								</span>
							}>
							<IconCircleInfo />
						</CustomToolTip>

						{(remainingPacks?.availableSubscriptionsCount ?? 0) > 0 ? (
							<span className="price-calc__label price-calc__label--pro">
								<IconPro
									fill="#1A2228"
									height={12}
									width={15}
								/>
								<span className="price-calc__label__text">
									{remainingPacks?.availableSubscriptionsCount}{' '}
									{(remainingPacks?.availableSubscriptionsCount ?? 0) === 1
										? t('price_comparator.step_detail.pack_available')
										: t('price_comparator.step_detail.packs_available')}
								</span>
							</span>
						) : (
							<span className="price-calc__label">
								<span className="price-calc__label__text">{t('price_comparator.step_detail.pack_name')}</span>
							</span>
						)}
					</div>
				</div>
			</div>
			<div className="price-comp__row">
				<div className="price-comp__flex">
					<div className="price-comp__main">
						<div className="price-comp__head">
							<button
								type="button"
								className="price-comp__back"
								onClick={() => setCurrentStep('saved')}>
								<IconBack />
							</button>
						</div>
						<div className="price-comp__body">
							{formData.products.map((product, productIndex) => (
								<div
									key={productIndex}
									className="price-comp-datails__item">
									<div className="price-comp-datails__header">
										<h3 className="price-comp-datails__title">
											{t('price_comparator.step_detail.product_title')} {productIndex + 1}
										</h3>
									</div>

									<div className="price-comp-datails__row">
										<div className="price-comp-datails__field">
											<div className="label-container">
												<input
													type="text"
													className={`label-container__input input-label ${errors.products[productIndex]?.productName ? 'error' : ''}`}
													value={product.productName}
													onChange={(e) => handleInputChange(productIndex, 'productName', e.target.value)}
												/>
												<label className="label-input">{t('price_comparator.step_detail.product_name')}</label>
												{errors.products[productIndex]?.productName && (
													<span className="error-message">{errors.products[productIndex].productName}</span>
												)}
											</div>
										</div>
									</div>

									<div className="price-comp-datails__row price-comp-datails__row--half">
										<div className="price-comp-datails__col">
											<div className="price-comp-datails__field">
												<div className="label-container">
													<input
														type="text"
														className="label-container__input input-label"
														value={product.containerType}
														onChange={(e) => handleInputChange(productIndex, 'containerType', e.target.value)}
													/>
													<label className="label-input">{t('price_comparator.step_detail.container_type')}</label>
												</div>
											</div>
										</div>

										<div className="price-comp-datails__col price-comp-datails__col--flex">
											<div className="price-comp-datails__field-with-icon">
												<div className="price-comp-datails__field">
													<div className="label-container">
														<input
															type="text"
															className={`label-container__input input-label ${errors.products[productIndex]?.tariffCode ? 'error' : ''}`}
															value={product.tariffCode}
															onChange={(e) => handleInputChange(productIndex, 'tariffCode', e.target.value)}
														/>
														<label className="label-input">{t('price_comparator.step_detail.tarif_code')}</label>

														{!validatedTariffCodes.includes(productIndex) ? (
															<button
																onClick={() => handleOpenTarifPopup(productIndex)}
																className="tarif-code_button"
																type="button"
																disabled={isLoadingTarif && currentValidatingProductIndex === productIndex}>
																{isLoadingTarif && currentValidatingProductIndex === productIndex ? (
																	<CircularLoading />
																) : (
																	<>{t('price_comparator.step_detail.tarif_code_button')}</>
																)}
															</button>
														) : (
															<span
																className="tarif-code_button tarif-code_button-succes"
																onClick={() => handleOpenTarifPopup(productIndex)}>
																<IconCkeckSolid
																	width={17}
																	height={20}
																	fill="#1A2228"
																/>
															</span>
														)}
													</div>
													{errors.products[productIndex]?.tariffCode && (
														<span className="error-message">{errors.products[productIndex].tariffCode}</span>
													)}
													<a
														href={`https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp?Lang=${lang}`}
														className="price-comp-datails__field-link"
														target="_blank"
														rel="noreferrer"
														title={t('price_comparator.step_detail.tarif_code_link')}>
														{t('price_comparator.step_detail.tarif_code_link')}
													</a>
												</div>
											</div>
											<div className="icon-container">
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_4.costTransportationWholesalerRetailerUsd.tooltip')}>
													<IconCircleHelper />
												</CustomToolTip>
											</div>
										</div>
									</div>

									<div className="price-comp-datails__row price-comp-datails__row--half">
										<div className="price-comp-datails__col price-comp-datails__col--flex">
											<div className="price-comp-datails__field price-comp-datails__col-unit">
												<div className="label-container">
													<label className="label-input">{t('price_comparator.step_detail.unit')}</label>
													<select
														className="label-container__input input-label-select"
														value={product.unit}
														onChange={(e) => handleInputChange(productIndex, 'unit', e.target.value)}>
														<option value={t('price_comparator.step_detail.unit_type.one')}>
															{t('price_comparator.step_detail.unit_type.one')}
														</option>
														<option value={t('price_comparator.step_detail.unit_type.two')}>
															{t('price_comparator.step_detail.unit_type.two')}
														</option>
														<option value={t('price_comparator.step_detail.unit_type.three')}>
															{t('price_comparator.step_detail.unit_type.three')}
														</option>
													</select>
													<IconDropDownArrow className="input-label-select-arrow" />
												</div>
											</div>
											<div className="price-comp-datails__field price-comp-datails__col-data">
												<div className="label-container">
													<input
														type="number"
														className={`label-container__input input-label ${errors.products[productIndex]?.weightVolume ? 'error' : ''}`}
														value={product.weightVolume}
														onChange={(e) => handleInputChange(productIndex, 'weightVolume', e.target.value)}
													/>
													<label className="label-input">{t('price_comparator.step_detail.weight_volume')}</label>
													{errors.products[productIndex]?.weightVolume && (
														<span className="error-message">{errors.products[productIndex].weightVolume}</span>
													)}
												</div>
											</div>
										</div>

										<div className="price-comp-datails__col">
											<div className="price-comp-datails__field">
												<div className="label-container">
													<input
														type="number"
														className={`label-container__input input-label ${errors.products[productIndex]?.price ? 'error' : ''}`}
														value={product.price}
														onChange={(e) => handleInputChange(productIndex, 'price', e.target.value)}
													/>
													<label className="label-input">{t('price_comparator.step_detail.price_without_iva')}</label>
													{errors.products[productIndex]?.price && (
														<span className="error-message">{errors.products[productIndex].price}</span>
													)}
												</div>
											</div>

											<Link
												to="/calcularprecios"
												className="price-comp-datails__field-link"
												rel="noreferrer"
												target="_blank"
												title={t('price_comparator.step_detail.price_without_iva_link')}>
												{t('price_comparator.step_detail.price_without_iva_link')}
											</Link>
										</div>
									</div>
								</div>
							))}

							<div className="price-comp-datails__row price-comp-datails__countries">
								<h3 className="price-comp-datails__title">{t('price_comparator.step_detail.countries_title')}</h3>
								<div className="price-comp-datails__row">
									<div className="label-container">
										<label className="label-input">{t('price_comparator.step_detail.countries_button')}</label>
										{loading ? (
											<div className="loading-container">
												<CircularLoading />
												<span>{t('common.loading')}</span>
											</div>
										) : error ? (
											<div className="error-container">
												<span className="error-message">{t('common.error_loading_data')}</span>
											</div>
										) : (
											<CustomMultiSelect
												data={transformedCountries}
												placeholder=""
												width="100%"
												onChange={handleCountriesChange}
												classes="price-comp-datails__multi-select"
											/>
										)}
										{errors.selectedCountries && <span className="error-message">{errors.selectedCountries}</span>}
									</div>
								</div>
							</div>
						</div>
						<div className="price-comp__footer">
							<button
								type="button"
								onClick={handleClickOpen}
								className="price-comp__footer__legal">
								{t('price_comparator.step_detail.legal_button')}
							</button>
							<button
								type="button"
								onClick={handleComparePrices}
								className="price-comp__footer__next price-comp__footer__next--primary">
								{t('price_comparator.step_detail.compare_prices')}
							</button>
						</div>
					</div>
				</div>
				<div className="price-comp__fixed">
					<VideoComponent
						showSrcImage={true}
						urlImagen={null}
					/>
				</div>
			</div>

			<PopUp
				classes="price-comp__resume__popup"
				isOpen={isOpenResume}
				onClose={() => {
					setIsOpenResume(false);
				}}>
				{isLoadingCompare ? (
					<div className="price-comp__loading">
						<CircularLoading />
					</div>
				) : (
					<div className="price-comp__resume">
						<h3 className="price-comp__resume__title">{t('price_comparator.popup_resume.title')}</h3>
						<h4 className="price-comp__resume__subtitle">{t('price_comparator.popup_resume.subtitle')}</h4>

						<div className="price-comp__resume__items">
							{formData.products.map((product, index) => (
								<div
									key={index}
									className="price-comp__resume__item">
									<div className="price-comp__resume__item__title">
										<span>{product.productName}</span>
									</div>
									<div className="price-comp__resume__item__data">
										{t('price_comparator.popup_resume.container_type')}: {product.containerType}
									</div>
									<div className="price-comp__resume__item__data">
										{t('price_comparator.popup_resume.tariff_code')}: {product.tariffCode}
									</div>
									<div className="price-comp__resume__item__data">
										{t('price_comparator.popup_resume.weight_volume')}: {product.weightVolume} {product.unit}
									</div>
									<div className="price-comp__resume__item__data">
										{t('price_comparator.popup_resume.price_without_iva')}: € {product.price}
									</div>
								</div>
							))}
						</div>

						<h4 className="price-comp__resume__subtitle">{t('price_comparator.popup_resume.countries_title')}</h4>

						<div className="price-comp__resume__countries">
							{formData.selectedCountries.map((country, index) => (
								<div
									key={index}
									className="price-comp__resume__country">
									{country.label || country.value || country}
								</div>
							))}
						</div>

						<div className="price-comp__resume__footer">
							<button
								className="price-comp__resume__footer__button price-comp__resume__footer__button--secondary"
								onClick={() => {
									setIsOpenResume(false);
								}}>
								{t('price_comparator.popup_resume.button_back')}
							</button>
							<button
								onClick={handleConfirmAndCompare}
								disabled={isLoadingCompare}
								className="price-comp__resume__footer__button price-comp__resume__footer__button--primary">
								{t('price_comparator.popup_resume.confirm_and_compare')}
							</button>
						</div>
					</div>
				)}
			</PopUp>

			<PopUp
				classes="price-comp__success__popup"
				isOpen={isOpenSuccess}
				onClose={() => {
					setIsOpenSuccess(false);
				}}>
				<div className="price-comp__success">
					<IconComparadorSucces className="price-comp__success__icon" />
					<h3 className="price-comp__success__title">{t('price_comparator.popup_success.title')}</h3>
					<div
						className="price-comp__success__body"
						dangerouslySetInnerHTML={{ __html: t('price_comparator.popup_success.body') }}
					/>

					<div className="price-comp__success__actions">
						<button
							className="price-comp__success__actions__button price-comp__success__actions__button--primary"
							onClick={() => {
								setIsOpenSuccess(false);
								setCurrentStep('saved');
							}}>
							{t('price_comparator.popup_success.button_back')}
						</button>
					</div>
				</div>
			</PopUp>

			{/* Popup */}
			<PopUp
				isOpen={isPopupTarifOpen}
				onClose={handleCloseTarifPopup}>
				{isLoadingTarif ? (
					<CircularLoading />
				) : isValidTarif ? (
					<div className="modal-tc">
						<div className="modal-tc_icon">
							<IconCircleCheck />
						</div>
						<h4 className="modal-tc_title">
							{t('price_calculator.step_1.modal_tarif_code.success_title')}{' '}
							{formData.products[currentValidatingProductIndex].tariffCode}
						</h4>
						<div
							className="modal-tc_text"
							dangerouslySetInnerHTML={{ __html: tarifDescription || '' }}
						/>
						<button
							onClick={handleCloseTarifPopup}
							className="modal-tc_button"
							type="button">
							{t('price_calculator.step_1.modal_tarif_code.success_button')}
						</button>
					</div>
				) : (
					<div className="modal-tc">
						<div className="modal-tc_icon">
							<IconCircleExclamation
								width={42}
								height={42}
							/>
						</div>
						<h4 className="modal-tc_title">
							{t('price_calculator.step_1.modal_tarif_code.error_title')}{' '}
							{formData.products[currentValidatingProductIndex].tariffCode}
						</h4>
						<p className="modal-tc_text">{t('price_calculator.step_1.modal_tarif_code.error_description')}</p>
						<button
							onClick={handleCloseTarifPopup}
							className="modal-tc_button"
							type="button">
							{t('price_calculator.step_1.modal_tarif_code.error_button')}
						</button>
					</div>
				)}
			</PopUp>

			<ModalLegal
				open={open}
				handleClose={handleClose}
			/>
		</>
	);
};
